import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Grid, List, Eye, EyeOff, QrCode, Download, Copy, BarChart3, Calendar, Clock, ExternalLink, Filter, X, Smartphone, Printer, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const QRCodesManagement = () => {
  const { user, userRestaurants } = useAuth();
  const [qrCodes, setQrCodes] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingQrCode, setEditingQrCode] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showQrPreview, setShowQrPreview] = useState(false);
  const [previewQrCode, setPreviewQrCode] = useState(null);
  const [filters, setFilters] = useState({
    isActive: '',
    hasAccess: '',
    dateRange: ''
  });
  const [formData, setFormData] = useState({
    restaurantId: '',
    tableNumber: '',
    qrCodeUrl: '',
    isActive: true
  });

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // API Services
  const qrCodeService = {
    getAll: async () => {
      console.log('Calling QR codes API...');
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/menuqrcodes`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('QR codes data:', data);
        return data;
      } catch (error) {
        console.error('QR codes API error:', error);
        throw error;
      }
    },
    create: async (data) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/menuqrcodes`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        mode: 'cors',
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    },
      update: async (id, data) => {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_BASE_URL}/api/menuqrcodes/${id}`, {
              method: 'PUT',
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`
              },
              mode: 'cors',
              body: JSON.stringify(data)
          });

          if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
          }

          // Response body varsa JSON parse et, yoksa boş obje dön
          const text = await response.text();
          return text ? JSON.parse(text) : { success: true };
      },
    delete: async (id) => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/menuqrcodes/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        mode: 'cors'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.ok;
    }
  };

  const restaurantService = {
    getAll: async () => {
      if (!user) return [];
      
      try {
        const token = localStorage.getItem('authToken');
        let url = `${API_BASE_URL}/api/Restaurants`;
        
        // Convert numeric role to string for comparison
        const userRole = typeof user.role === 'number' ? 
          (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
          user.role;
        
        // If user is not SuperAdmin, filter by their restaurant
        if (userRole !== 'SuperAdmin') {
          url = `${API_BASE_URL}/api/Restaurants/user/${user.id}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          return data;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Restaurant API Error:', error);
        throw error;
      }
    }
  };



  // Load Data
  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('🔄 Fetching all data...');
      const [qrCodesData, restaurantsData] = await Promise.all([
        qrCodeService.getAll(),
        restaurantService.getAll()
      ]);
      
      console.log('✅ All data loaded successfully');
      setQrCodes(qrCodesData);
      
      // Eğer userRestaurants yoksa veya boşsa, API'den gelen restaurantsData'yı kullan
      if (!userRestaurants || !Array.isArray(userRestaurants) || userRestaurants.length === 0) {
        setRestaurants(restaurantsData);
      }
      
      // Eğer sadece 1 restaurant varsa, onu otomatik seç (formData'da restaurantId yoksa)
      const currentRestaurants = (userRestaurants && Array.isArray(userRestaurants) && userRestaurants.length > 0) ? userRestaurants : restaurantsData;
      if (Array.isArray(currentRestaurants) && currentRestaurants.length === 1) {
        const restaurantId = currentRestaurants[0].id || currentRestaurants[0].restaurantId || currentRestaurants[0].ID || currentRestaurants[0].RestaurantId;
        if (restaurantId) {
          setFormData(prev => {
            // Eğer zaten bir restaurantId varsa değiştirme
            if (prev.restaurantId) {
              return prev;
            }
            console.log('Otomatik restoran seçildi (fetchData):', restaurantId);
            return {
              ...prev,
              restaurantId: restaurantId
            };
          });
        }
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      setQrCodes([]);
      setRestaurants([]);
      alert(`API Hatası: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createQrCode = async () => {
    try {
      // Önce geçici URL ile QR kodu oluştur
      const tempUrl = `${window.location.origin}/ordermenu/menu/temp`;
      const qrData = {
        ...formData,
        qrCodeUrl: tempUrl,
        qrCodeImage: ''
      };

      const result = await qrCodeService.create(qrData);
      const newQrCodeId = result.id;

      // QR kod ID'si ile doğru URL'yi oluştur ve güncelle
      const menuUrl = `${window.location.origin}/ordermenu/menu/${newQrCodeId}`;
      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(menuUrl)}`;

      await qrCodeService.update(newQrCodeId, {
        ...formData,
        qrCodeUrl: menuUrl,
        qrCodeImage: qrImageUrl
      });

      await fetchData();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error creating QR code:', error);
      alert('QR kod oluşturulurken bir hata oluştu!');
    }
  };

const updateQrCode = async () => {
        if (!editingQrCode) return;

        // Tüm DTO field'larını dahil et
        const completeFormData = {
            restaurantId: formData.restaurantId,
            tableNumber: formData.tableNumber,
            qrCodeUrl: formData.qrCodeUrl,
            qrCodeImage: formData.qrCodeImage || editingQrCode.qrCodeImage || '', // Eksik field'ı ekle
            lastAccessedDate: formData.lastAccessedDate || editingQrCode.lastAccessedDate,
            accessCount: formData.accessCount || editingQrCode.accessCount || 0,
            isActive: formData.isActive
        };

        console.log('editingQrCode:', editingQrCode);
        console.log('completeFormData:', completeFormData);

        try {
            await qrCodeService.update(editingQrCode.id, completeFormData);
            await fetchData();
            resetForm();
            setShowModal(false);
            setEditingQrCode(null);
        } catch (error) {
            console.error('Error updating QR code:', error);
            alert('QR kod güncellenirken bir hata oluştu!');
        }
    };

  const deleteQrCode = async (id) => {
    if (window.confirm('Bu QR kodu silmek istediğinizden emin misiniz?')) {
      try {
        await qrCodeService.delete(id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting QR code:', error);
        alert('QR kod silinirken bir hata oluştu!');
      }
    }
  };

  const toggleQrCodeStatus = async (qrCode) => {
    try {
      const updatedData = { ...qrCode, isActive: !qrCode.isActive };
      await qrCodeService.update(qrCode.id, updatedData);
      await fetchData();
    } catch (error) {
      console.error('Error toggling QR code status:', error);
      alert('QR kod durumu değiştirilirken bir hata oluştu!');
    }
  };

  const resetForm = () => {
    setFormData({
      restaurantId: '',
      tableNumber: '',
      qrCodeUrl: '',
      isActive: true
    });
  };

  const openModal = (qrCode) => {
    if (qrCode) {
      setEditingQrCode(qrCode);
      setFormData({
        restaurantId: qrCode.restaurantId,
        tableNumber: qrCode.tableNumber || '',
        qrCodeUrl: qrCode.qrCodeUrl,
        isActive: qrCode.isActive
      });
    } else {
      setEditingQrCode(null);
      resetForm();
    }
    setShowModal(true);
  };

  const openQrPreview = (qrCode) => {
    setPreviewQrCode(qrCode);
    setShowQrPreview(true);
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find(r => r.id === restaurantId);
    return restaurant ? restaurant.name : 'Bilinmeyen Restoran';
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Kopyalandı!');
    } catch (err) {
      console.error('Kopyalama hatası:', err);
    }
  };

    const downloadQrCode = async (qrCode) => {
        try {
            const imageUrl = qrCode.qrCodeImage || `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrCode.qrCodeUrl)}`;

            // Resmi fetch et
            const response = await fetch(imageUrl);
            if (!response.ok) throw new Error('Resim indirilemedi');

            // Blob'a çevir
            const blob = await response.blob();

            // Download link oluştur
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `QR-Masa-${qrCode.tableNumber}-${getRestaurantName(qrCode.restaurantId)}.png`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Memory temizle
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('QR kod indirme hatası:', error);
            alert('QR kod indirilemedi. Lütfen tekrar deneyin.');
        }
    };

  const formatDate = (dateString) => {
    if (!dateString) return 'Hiç erişilmemiş';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAccessFrequency = (accessCount, createdDate) => {
    if (!accessCount || accessCount === 0) return 'Hiç kullanılmamış';
    
    const daysSinceCreated = Math.max(1, Math.ceil((new Date() - new Date(createdDate)) / (1000 * 60 * 60 * 24)));
    const frequency = accessCount / daysSinceCreated;
    
    if (frequency < 0.1) return 'Çok Düşük';
    if (frequency < 0.5) return 'Düşük';
    if (frequency < 2) return 'Orta';
    if (frequency < 5) return 'Yüksek';
    return 'Çok Yüksek';
  };

  const getUsageColor = (accessCount) => {
    if (!accessCount || accessCount === 0) return 'bg-gray-100 text-gray-800';
    if (accessCount < 10) return 'bg-red-100 text-red-800';
    if (accessCount < 50) return 'bg-yellow-100 text-yellow-800';
    if (accessCount < 100) return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  const clearFilters = () => {
    setFilters({
      isActive: '',
      hasAccess: '',
      dateRange: ''
    });
    setSelectedRestaurant('');
    setSearchTerm('');
  };

  const filteredQrCodes = qrCodes.filter(qrCode => {
    // Text search
    const matchesSearch = (qrCode.tableNumber && qrCode.tableNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
      qrCode.qrCodeUrl.toLowerCase().includes(searchTerm.toLowerCase());

    // Restaurant filter
    const matchesRestaurant = !selectedRestaurant || qrCode.restaurantId === selectedRestaurant;

    // Advanced filters
    const matchesActive = filters.isActive === '' || 
      (filters.isActive === 'true' && qrCode.isActive) ||
      (filters.isActive === 'false' && !qrCode.isActive);

    const matchesAccess = filters.hasAccess === '' ||
      (filters.hasAccess === 'true' && qrCode.accessCount > 0) ||
      (filters.hasAccess === 'false' && (!qrCode.accessCount || qrCode.accessCount === 0));

    return matchesSearch && matchesRestaurant && matchesActive && matchesAccess;
  });

  const generateBulkQrCodes = async (restaurantId, startTable, endTable) => {
    try {
      for (let tableNum = startTable; tableNum <= endTable; tableNum++) {
        // Önce geçici URL ile oluştur
        const tempData = {
          restaurantId,
          tableNumber: tableNum.toString(),
          qrCodeUrl: 'temp',
          qrCodeImage: '',
          isActive: true
        };
        const result = await qrCodeService.create(tempData);
        const newId = result.id;

        // Doğru URL ile güncelle
        const menuUrl = `${window.location.origin}/ordermenu/menu/${newId}`;
        await qrCodeService.update(newId, {
          ...tempData,
          qrCodeUrl: menuUrl,
          qrCodeImage: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(menuUrl)}`
        });
      }

      await fetchData();
    } catch (error) {
      console.error('Error creating bulk QR codes:', error);
      alert('Toplu QR kod oluşturulurken bir hata oluştu!');
    }
  };

  useEffect(() => {
    const initializeRestaurants = async () => {
      // Önce userRestaurants kontrolü yap
      if (userRestaurants && Array.isArray(userRestaurants) && userRestaurants.length > 0) {
        setRestaurants(userRestaurants);
        
        // Eğer sadece 1 restaurant varsa, onu otomatik seç
        if (userRestaurants.length === 1) {
          const restaurantId = userRestaurants[0].id || userRestaurants[0].restaurantId || userRestaurants[0].ID || userRestaurants[0].RestaurantId;
          if (restaurantId) {
            setFormData(prev => ({
              ...prev,
              restaurantId: restaurantId
            }));
            console.log('Otomatik restoran seçildi (userRestaurants):', restaurantId);
          }
        }
      }
      
      // Sonra fetchData çağır (içinde de otomatik seçim var)
      await fetchData();
    };
    
    initializeRestaurants();
  }, [userRestaurants]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">QR Kod Yönetimi</h1>
          <p className="text-gray-600">Masa QR kodlarını oluşturun ve yönetin</p>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Masa numarası ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Restaurant Filter */}
              <select
                value={selectedRestaurant}
                onChange={(e) => setSelectedRestaurant(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                              <option value="">Tüm Restoranlar</option>
                              {restaurants.map(restaurant => (
                                  <option key={restaurant.id} value={restaurant.id}>
                                      {restaurant.name}
                                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              {/* Advanced Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 border border-gray-300 rounded-lg ${showFilters ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                title="Advanced Filters"
              >
                <Filter className="w-4 h-4" />
              </button>

              {/* Clear Filters */}
              <button
                onClick={clearFilters}
                className="p-2 border border-gray-300 rounded-lg bg-white text-gray-600 hover:bg-gray-50"
                title="Clear Filters"
              >
                <X className="w-4 h-4" />
              </button>

              {/* View Toggle */}
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {/* Add Button */}
              <button
                onClick={() => openModal()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Yeni QR Kod
              </button>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Durum</label>
                  <select
                    value={filters.isActive}
                    onChange={(e) => setFilters({...filters, isActive: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tümü</option>
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </div>

                {/* Access Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Erişim Durumu</label>
                  <select
                    value={filters.hasAccess}
                    onChange={(e) => setFilters({...filters, hasAccess: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tümü</option>
                    <option value="true">Kullanılmış</option>
                    <option value="false">Hiç Kullanılmamış</option>
                  </select>
                </div>

                {/* Bulk Actions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Toplu İşlemler</label>
                  <button
                    onClick={() => {
                      const restaurantId = prompt('Restoran ID:');
                      const startTable = parseInt(prompt('Başlangıç masa numarası:'));
                      const endTable = parseInt(prompt('Bitiş masa numarası:'));
                      if (restaurantId && startTable && endTable && startTable <= endTable) {
                        generateBulkQrCodes(restaurantId, startTable, endTable);
                      }
                    }}
                    className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    Toplu QR Oluştur
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <QrCode className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{qrCodes.length}</div>
                  <div className="text-sm text-gray-500">Toplam QR Kod</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {qrCodes.filter(q => q.isActive).length}
                  </div>
                  <div className="text-sm text-gray-500">Aktif QR Kod</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {qrCodes.reduce((sum, q) => sum + (q.accessCount || 0), 0)}
                  </div>
                  <div className="text-sm text-gray-500">Toplam Erişim</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Smartphone className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {qrCodes.filter(q => q.accessCount > 0).length}
                  </div>
                  <div className="text-sm text-gray-500">Kullanılan Kod</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* QR Codes Grid/List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="h-32 bg-gray-200 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        ) : filteredQrCodes.length === 0 ? (
          <div className="text-center py-12">
            <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">QR kod bulunamadı</h3>
            <p className="text-gray-500 mb-4">Arama kriterlerinize uygun QR kod bulunmuyor.</p>
            <button
              onClick={() => openModal()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              İlk QR kodu oluştur
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredQrCodes.map((qrCode) => (
              <div key={qrCode.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* QR Code Display */}
                  <div className="flex justify-center mb-4">
                    <div 
                      className="w-32 h-32 border-2 border-gray-200 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-300 transition-colors"
                      onClick={() => openQrPreview(qrCode)}
                    >
                      {qrCode.qrCodeImage ? (
                        <img 
                          src={qrCode.qrCodeImage} 
                          alt={`QR Kod - Masa ${qrCode.tableNumber}`}
                          className="w-full h-full object-contain rounded"
                        />
                      ) : (
                        <QrCode className="w-16 h-16 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Table Info */}
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Masa {qrCode.tableNumber || 'N/A'}
                    </h3>
                    <p className="text-sm text-gray-500">{getRestaurantName(qrCode.restaurantId)}</p>
                  </div>

                  {/* Stats */}
                  <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
                    <div className="text-center">
                      <div className="font-medium text-gray-900">{qrCode.accessCount || 0}</div>
                      <div className="text-xs">Erişim</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900">{formatDate(qrCode.lastAccessedDate).split(' ')[0]}</div>
                      <div className="text-xs">Son Erişim</div>
                    </div>
                  </div>

                  {/* Usage Badge */}
                  <div className="flex justify-center mb-4">
                    <span className={`text-xs px-3 py-1 rounded-full ${getUsageColor(qrCode.accessCount)}`}>
                      {getAccessFrequency(qrCode.accessCount, qrCode.createdDate)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => openQrPreview(qrCode)}
                      className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-1"
                      title="Görüntüle"
                    >
                      <Eye className="w-4 h-4" />
                      Görüntüle
                    </button>
                    <button
                      onClick={() => downloadQrCode(qrCode)}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                      title="İndir"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleQrCodeStatus(qrCode)}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                        qrCode.isActive 
                          ? 'bg-red-600 hover:bg-red-700 text-white' 
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                      title={qrCode.isActive ? 'Pasifleştir' : 'Aktifleştir'}
                    >
                      {qrCode.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Management Actions */}
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      qrCode.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {qrCode.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openModal(qrCode)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Düzenle"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteQrCode(qrCode.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 uppercase tracking-wider">
                <div className="col-span-2">QR Kod</div>
                <div className="col-span-2">Masa</div>
                <div className="col-span-2">Restoran</div>
                <div className="col-span-2">Erişim Sayısı</div>
                <div className="col-span-2">Son Erişim</div>
                <div className="col-span-1">Durum</div>
                <div className="col-span-1">İşlemler</div>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredQrCodes.map((qrCode) => (
                <div key={qrCode.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-2">
                      <div 
                        className="w-12 h-12 border border-gray-200 rounded cursor-pointer hover:border-blue-300 transition-colors"
                        onClick={() => openQrPreview(qrCode)}
                      >
                        {qrCode.qrCodeImage ? (
                          <img 
                            src={qrCode.qrCodeImage} 
                            alt={`QR Kod`}
                            className="w-full h-full object-contain rounded"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <QrCode className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm font-medium text-gray-900">
                        Masa {qrCode.tableNumber || 'N/A'}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-sm text-gray-600">{getRestaurantName(qrCode.restaurantId)}</span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{qrCode.accessCount || 0}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getUsageColor(qrCode.accessCount)}`}>
                          {getAccessFrequency(qrCode.accessCount, qrCode.createdDate)}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs text-gray-500">
                        {formatDate(qrCode.lastAccessedDate)}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        qrCode.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {qrCode.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <div className="flex gap-1">
                        <button
                          onClick={() => openQrPreview(qrCode)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Görüntüle"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => downloadQrCode(qrCode)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="İndir"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openModal(qrCode)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Düzenle"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteQrCode(qrCode.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingQrCode ? 'QR Kod Düzenle' : 'Yeni QR Kod Ekle'}
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Restaurant Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Restoran *
                  </label>
                  <select
                    value={formData.restaurantId}
                    onChange={(e) => setFormData({...formData, restaurantId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                                      <option value="">Tüm Restoranlar</option>
                                      {restaurants.map(restaurant => (
                                          <option key={restaurant.id} value={restaurant.id}>
                                              {restaurant.name}
                                          </option>
                    ))}
                  </select>
                </div>

                {/* Table Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Masa Numarası *
                  </label>
                  <input
                    type="text"
                    value={formData.tableNumber}
                    onChange={(e) => setFormData({...formData, tableNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1, 2, 3, A1, VIP-1..."
                    required
                  />
                </div>

                {/* Custom URL (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Özel URL (Opsiyonel)
                  </label>
                  <input
                    type="url"
                    value={formData.qrCodeUrl}
                    onChange={(e) => setFormData({...formData, qrCodeUrl: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Boş bırakılırsa otomatik oluşturulur"
                  />
                </div>

                {/* QR Code Preview */}
                {formData.restaurantId && formData.tableNumber && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-3">QR Kod Önizlemesi</h4>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 border border-blue-300 rounded-lg bg-white flex items-center justify-center">
                        <QrCode className="w-16 h-16 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm">
                          <div className="font-medium text-blue-900">Masa: {formData.tableNumber}</div>
                          <div className="text-blue-700">{getRestaurantName(formData.restaurantId)}</div>
                          <div className="text-xs text-blue-600 mt-1 break-all">
                            {formData.qrCodeUrl || 'QR kod oluşturulduğunda URL otomatik atanacak'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Aktif QR Kod</span>
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    Pasif QR kodlar tarandığında menüye yönlendirilmez
                  </p>
                </div>

                {/* QR Code Info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">📱 QR Kod Nasıl Çalışır?</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div>• Müşteriler QR kodu tarayarak doğrudan menüye erişir</div>
                    <div>• Her tarama otomatik olarak kaydedilir ve istatistiklerde görünür</div>
                    <div>• QR kod şekilde indirilebilir ve masalara yerleştirilebilir</div>
                    <div>• Her masa için benzersiz QR kod ve URL oluşturulur</div>
                  </div>
                </div>
 
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingQrCode(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={editingQrCode ? updateQrCode : createQrCode}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                {editingQrCode ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Preview Modal */}
      {showQrPreview && previewQrCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  QR Kod - Masa {previewQrCode.tableNumber}
                </h2>
                <button
                  onClick={() => setShowQrPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="text-center space-y-4">
                {/* Large QR Code */}
                <div className="flex justify-center">
                  <div className="w-64 h-64 border-2 border-gray-200 rounded-lg bg-white flex items-center justify-center">
                    {previewQrCode.qrCodeImage ? (
                      <img 
                        src={previewQrCode.qrCodeImage.replace('200x200', '300x300')} 
                        alt={`QR Kod - Masa ${previewQrCode.tableNumber}`}
                        className="w-full h-full object-contain rounded"
                      />
                    ) : (
                      <QrCode className="w-32 h-32 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* QR Info */}
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getRestaurantName(previewQrCode.restaurantId)}
                  </h3>
                  <p className="text-gray-600">Masa {previewQrCode.tableNumber}</p>
                  <p className="text-sm text-gray-500 break-all">{previewQrCode.qrCodeUrl}</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 py-4 border-t border-gray-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{previewQrCode.accessCount || 0}</div>
                    <div className="text-sm text-gray-500">Toplam Erişim</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(previewQrCode.lastAccessedDate)}
                    </div>
                    <div className="text-sm text-gray-500">Son Erişim</div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => downloadQrCode(previewQrCode)}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    İndir
                  </button>
                  <button
                    onClick={() => copyToClipboard(previewQrCode.qrCodeUrl)}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    URL Kopyala
                  </button>
                  <button
                    onClick={() => window.open(previewQrCode.qrCodeUrl, '_blank')}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodesManagement;